import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Navigate } from "react-router-dom";
import { num } from "@/lib/constants";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sparkles, MessageSquare, CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function AdminPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [ai, setAi] = useState(null);
  const [aiKey, setAiKey] = useState("");
  const [smsStatus, setSmsStatus] = useState(null);
  const [smsTo, setSmsTo] = useState("");
  const [smsMsg, setSmsMsg] = useState("Test SMS from The Close Out — auction alerts are working.");
  const [smsSending, setSmsSending] = useState(false);

  useEffect(() => {
    if (user?.role !== "admin") return;
    api.get("/admin/users").then((r) => setUsers(r.data.items || []));
    api.get("/admin/stats").then((r) => setStats(r.data));
    api.get("/admin/contact-requests").then((r) => setContacts(r.data.items || []));
    api.get("/admin/ai-provider").then((r) => { setAi(r.data); setAiKey(`${r.data.current.provider}|${r.data.current.model}`); });
    api.get("/admin/sms/status").then((r) => setSmsStatus(r.data)).catch(() => {});
  }, [user]);

  if (user?.role !== "admin") return <Navigate to="/app/dashboard" />;

  const switchAi = async (key) => {
    const [provider, model] = key.split("|");
    try {
      await api.post("/admin/ai-provider", { provider, model });
      setAiKey(key);
      toast.success(`AI provider switched to ${provider} · ${model}`);
    } catch { toast.error("Switch failed"); }
  };

  const sendTestSms = async () => {
    if (!smsTo.startsWith("+")) { toast.error("Phone must be E.164 format like +15551234567"); return; }
    setSmsSending(true);
    try {
      const r = await api.post("/admin/sms/test", { to: smsTo, message: smsMsg });
      if (r.data.ok) toast.success(`SMS sent! Twilio SID: ${r.data.sid}`);
      else toast.error(`Failed: ${r.data.reason || "unknown"}`);
    } catch { toast.error("Request failed"); } finally { setSmsSending(false); }
  };

  return (
    <div className="space-y-5" data-testid="admin-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#d4a574] mb-2 font-semibold">Admin</div>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Platform Control.</h1>
      </div>

      <div className="border border-[#33291f] rounded-lg bg-[#1c1712] p-4 space-y-2">
        <div className="font-display text-lg font-semibold">Admin Tools</div>
        <div className="flex flex-wrap gap-2">
          <a href="/app/admin/revenue" data-testid="admin-revenue-link" className="text-[12px] px-3 py-1.5 rounded bg-[#241d16] border border-[#4a3a2b] hover:border-[#d4a574] text-[#d4a574]">Revenue & MRR →</a>
          <a href="/app/admin/data-sources" data-testid="admin-datasources-link" className="text-[12px] px-3 py-1.5 rounded bg-[#241d16] border border-[#4a3a2b] hover:border-[#d4a574] text-[#d4a574]">Data Sources & Ingestion →</a>
          <a href="/app/admin/audit-logs" data-testid="admin-audit-link" className="text-[12px] px-3 py-1.5 rounded bg-[#241d16] border border-[#4a3a2b] hover:border-[#d4a574] text-[#d4a574]">Audit Logs →</a>
          <a href="/app/admin/promo-codes" data-testid="admin-promos-link" className="text-[12px] px-3 py-1.5 rounded bg-[#241d16] border border-[#4a3a2b] hover:border-[#d4a574] text-[#d4a574]">Promo Codes →</a>
        </div>
      </div>

      {/* SMS Test Panel */}
      <div className="border border-[#33291f] rounded-lg bg-[#1c1712] p-5" data-testid="sms-test-card">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
          <div>
            <div className="font-display text-lg font-semibold flex items-center gap-2"><MessageSquare size={16} className="text-[#d4a574]" /> Twilio SMS</div>
            <div className="text-[12px] text-[#b8ac96] mt-1">Auction & alert SMS delivery. Add TWILIO_ACCOUNT_SID / TWILIO_AUTH_TOKEN / TWILIO_PHONE in backend/.env to enable.</div>
          </div>
          <div className="text-[11px] font-mono uppercase tracking-wider">
            {smsStatus?.configured ? (
              <span className="flex items-center gap-1 text-[#34d399]" data-testid="sms-configured"><CheckCircle2 size={12} /> Configured</span>
            ) : (
              <span className="flex items-center gap-1 text-[#7a6d5a]" data-testid="sms-not-configured"><XCircle size={12} /> Not configured (mock mode)</span>
            )}
          </div>
        </div>
        <div className="grid md:grid-cols-3 gap-2 items-end">
          <div className="md:col-span-1">
            <label className="text-[10px] uppercase tracking-wider text-[#7a6d5a] block mb-1">To (E.164)</label>
            <input value={smsTo} onChange={(e) => setSmsTo(e.target.value)} placeholder="+15551234567" data-testid="sms-to" className="w-full bg-[#241d16] border border-[#33291f] rounded px-3 py-2 text-[13px] font-mono focus:outline-none focus:border-[#d4a574]" />
          </div>
          <div className="md:col-span-2">
            <label className="text-[10px] uppercase tracking-wider text-[#7a6d5a] block mb-1">Message</label>
            <input value={smsMsg} onChange={(e) => setSmsMsg(e.target.value)} data-testid="sms-message" className="w-full bg-[#241d16] border border-[#33291f] rounded px-3 py-2 text-[13px] focus:outline-none focus:border-[#d4a574]" />
          </div>
        </div>
        <button onClick={sendTestSms} disabled={smsSending || !smsTo} data-testid="send-test-sms-btn" className="mt-3 flex items-center gap-2 px-4 py-2 rounded bg-[#d4a574] text-[#14100c] font-semibold text-[12px] hover:bg-[#e8b988] disabled:opacity-60">
          {smsSending ? <Loader2 size={13} className="animate-spin" /> : <MessageSquare size={13} />} Send Test SMS
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {stats && [
          { label: "Users", v: stats.users },
          { label: "Opportunities", v: stats.opportunities },
          { label: "Leads", v: stats.leads },
          { label: "Demo Requests", v: stats.contact_requests },
          { label: "Saved Searches", v: stats.saved_searches },
        ].map((s) => (
          <div key={s.label} className="kpi-frame rounded-lg p-4">
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#7a6d5a] font-semibold mb-2">{s.label}</div>
            <div className="font-display text-2xl font-bold text-[#d4a574]">{num(s.v)}</div>
          </div>
        ))}
      </div>

      <div className="border border-[#33291f] rounded-lg bg-[#1c1712] p-5" data-testid="ai-provider-card">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="font-display text-lg font-semibold flex items-center gap-2"><Sparkles size={16} className="text-[#d4a574]" /> AI Provider</div>
            <div className="text-[12px] text-[#b8ac96] mt-1">Switch the LLM used for scoring, natural-language search, investment analysis, and chat responses. Changes take effect immediately for new requests.</div>
          </div>
          <div className="text-[10px] font-mono uppercase tracking-wider text-[#7a6d5a]">Current: <span className="text-[#d4a574]">{ai?.current?.provider} · {ai?.current?.model}</span></div>
        </div>
        <Select value={aiKey} onValueChange={switchAi}>
          <SelectTrigger data-testid="ai-provider-select" className="bg-[#241d16] border-[#33291f]"><SelectValue placeholder="Select provider…" /></SelectTrigger>
          <SelectContent>
            {(ai?.options || []).map((o) => (
              <SelectItem key={`${o.provider}|${o.model}`} value={`${o.provider}|${o.model}`} data-testid={`ai-opt-${o.provider}-${o.model}`}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="text-[10px] text-[#7a6d5a] mt-2 font-mono">All models bill against your Emergent LLM Key balance. Higher-tier models (Opus, GPT-5.6) cost more per request.</div>
      </div>

      <div className="border border-[#33291f] rounded-lg bg-[#1c1712] overflow-hidden" data-testid="business-info-card">
        <div className="p-4 border-b border-[#33291f]"><div className="font-display text-lg font-semibold">Business Info</div></div>
        <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-[13px] p-5">
          <div><div className="text-[10px] uppercase tracking-wider text-[#7a6d5a]">Legal Name</div><div className="font-semibold">THE CLOSE OUT</div></div>
          <div><div className="text-[10px] uppercase tracking-wider text-[#7a6d5a]">DBA</div><div>The Close Out</div></div>
          <div><div className="text-[10px] uppercase tracking-wider text-[#7a6d5a]">EIN</div><div className="font-mono text-[#d4a574]">42-4006558</div></div>
          <div><div className="text-[10px] uppercase tracking-wider text-[#7a6d5a]">Issued</div><div className="font-mono text-[11px]">July 22, 2026</div></div>
          <div><div className="text-[10px] uppercase tracking-wider text-[#7a6d5a]">Entity</div><div>Single-Member LLC</div></div>
          <div><div className="text-[10px] uppercase tracking-wider text-[#7a6d5a]">Care Of</div><div>Jonathon M Norwood Sole MBR</div></div>
          <div className="col-span-2"><div className="text-[10px] uppercase tracking-wider text-[#7a6d5a]">Address</div><div>12606 225 St E · Graham, WA 98338 · US</div></div>
          <div><div className="text-[10px] uppercase tracking-wider text-[#7a6d5a]">Responsible Party</div><div>Jonathon M Norwood</div></div>
          <div><div className="text-[10px] uppercase tracking-wider text-[#7a6d5a]">Title</div><div>Owner / Sole Member</div></div>
        </div>
      </div>

      <div className="border border-[#33291f] rounded-lg bg-[#1c1712] overflow-hidden">
        <div className="p-4 border-b border-[#33291f]"><div className="font-display text-lg font-semibold">Users</div></div>
        <table className="w-full text-[13px]" data-testid="admin-users-table">
          <thead className="bg-[#241d16] text-[10px] uppercase tracking-wider text-[#7a6d5a]">
            <tr><th className="text-left px-4 py-2">Name</th><th className="text-left px-4 py-2">Email</th><th className="text-left px-4 py-2">Company</th><th className="text-left px-4 py-2">Role</th><th className="text-left px-4 py-2">Joined</th></tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-[#33291f]">
                <td className="px-4 py-2">{u.full_name}</td>
                <td className="px-4 py-2 font-mono text-[12px] text-[#b8ac96]">{u.email}</td>
                <td className="px-4 py-2 text-[#b8ac96]">{u.company || "—"}</td>
                <td className="px-4 py-2"><span className="text-[10px] uppercase px-2 py-0.5 rounded bg-[#241d16] border border-[#4a3a2b] text-[#d4a574]">{u.role}</span></td>
                <td className="px-4 py-2 font-mono text-[11px] text-[#7a6d5a]">{u.created_at?.slice(0, 10)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="border border-[#33291f] rounded-lg bg-[#1c1712] overflow-hidden">
        <div className="p-4 border-b border-[#33291f]"><div className="font-display text-lg font-semibold">Demo Requests</div></div>
        {contacts.length === 0 ? (
          <div className="p-6 text-center text-[#7a6d5a] text-sm">No demo requests yet.</div>
        ) : (
          <table className="w-full text-[13px]">
            <thead className="bg-[#241d16] text-[10px] uppercase tracking-wider text-[#7a6d5a]">
              <tr><th className="text-left px-4 py-2">Name</th><th className="text-left px-4 py-2">Email</th><th className="text-left px-4 py-2">Company</th><th className="text-left px-4 py-2">Message</th></tr>
            </thead>
            <tbody>
              {contacts.map((c) => (
                <tr key={c.id} className="border-t border-[#33291f]">
                  <td className="px-4 py-2">{c.name}</td>
                  <td className="px-4 py-2 font-mono text-[12px] text-[#b8ac96]">{c.email}</td>
                  <td className="px-4 py-2 text-[#b8ac96]">{c.company || "—"}</td>
                  <td className="px-4 py-2 text-[#b8ac96] max-w-md truncate">{c.message}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
